/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import Cookies from 'js-cookie';
import { CookieStorageProvider, StorageProvider } from 'src/core/types/StorageProvider';

/**
 * An overloaded function to get the correct storage provider
 * based on the given storage type parameter.
 *
 * @param storageType The storage type to get.
 * @returns A storage provider matching the `storageType`
 * parameter given.
 */
export function getStorage(storageType: 'cookie'): CookieStorageProvider;
export function getStorage(storageType: 'localStorage' | 'sessionStorage'): StorageProvider;
export function getStorage(storageType: 'cookie' | 'localStorage' | 'sessionStorage'): StorageProvider {
    switch (storageType) {
        case 'localStorage':
            return localStorageProvider;
        case 'sessionStorage':
            return sessionStorageProvider;
        case 'cookie':
        default:
            return cookieProvider;
    }
}

/**
 * Represents a provider for manipulating data
 * via cookies as the storage method.
 */
const cookieProvider: CookieStorageProvider = {
    clear: () => Object.keys(Cookies.get()).forEach(key => Cookies.remove(key)),
    getData: key => Cookies.get(key),
    getSize: () => Object.keys(Cookies.get()).length,
    hasData: key => !!Cookies.get(key),
    removeData: key => Cookies.remove(key),
    setData: (key, value, options) => Cookies.set(key, value, options)
};

/**
 * Represents a provider for manipulating data
 * via local storage as the storage method.
 */
const localStorageProvider: StorageProvider = {
    clear: () => window.localStorage.clear(),
    getData: key => window.localStorage.getItem(key),
    getSize: () => window.localStorage.length,
    hasData: key => !!window.localStorage.getItem(key),
    removeData: key => window.localStorage.removeItem(key),
    setData: (key, value) => window.localStorage.setItem(key, value)
};

/**
 * Represents a provider for manipulating data
 * via session storage as the storage method.
 */
const sessionStorageProvider: StorageProvider = {
    clear: () => window.sessionStorage.clear(),
    getData: key => window.sessionStorage.getItem(key),
    getSize: () => window.sessionStorage.length,
    hasData: key => !!window.sessionStorage.getItem(key),
    removeData: key => window.sessionStorage.removeItem(key),
    setData: (key, value) => window.sessionStorage.setItem(key, value)
};
